import {Collapse, Icon, IconButton, ListItemText} from '@mui/material';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import AppIcon from 'main/core/AppLayout/components/AppIcon';
import {useThemeContext} from 'main/utility/AppContextProvider/ThemeContextProvider';
import {useAuthUser} from 'main/utility/AuthHooks';
import {checkPermission} from 'main/utility/helper/RouteHelper';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {BUSINESS_ROLE} from 'shared/constants/AppConst';
import VerticalItem from '../VerticalItem';
import VerticalCollapseItem from './VerticalCollapseItem';

const needsToBeOpened = (pathname, item) => {
  return pathname && isUrlInChildren(item, pathname);
};

const isUrlInChildren = (parent, url) => {
  if (!parent.children) {
    return false;
  }

  for (let i = 0; i < parent.children.length; i++) {
    if (parent.children[i].children) {
      if (isUrlInChildren(parent.children[i], url)) {
        return true;
      }
    }

    if (
      parent.children[i].url === url ||
      url.includes(parent.children[i].url)
    ) {
      return true;
    }
  }

  return false;
};

const VerticalCollapse = ({item, level}) => {
  const {theme} = useThemeContext();
  const {pathname} = useLocation();
  const [open, setOpen] = useState(() => needsToBeOpened(pathname, item));

  useEffect(() => {
    if (needsToBeOpened(pathname, item)) {
      setOpen(true);
    }
  }, [pathname, item]);

  const handleClick = () => {
    setOpen(!open);
  };

  const {user} = useAuthUser();
  const hasPermission = useMemo(
    () => checkPermission(item.permittedrole, user.role),
    [item.permittedrole, user.role],
  );

  if (!hasPermission) {
    return null;
  }

  return (
    <>
      <VerticalCollapseItem
        level={level}
        button
        component='div'
        className={clsx('menu-vertical-collapse', open && 'open')}
        onClick={handleClick}
      >
        {item.icon && (
          <Box component='span'>
            <Icon sx={{mr: 4}} color='action' className={clsx('nav-item-icon')}>
              <AppIcon name={item.icon} />
            </Icon>
          </Box>
        )}
        <ListItemText
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 14,
          }}
          className='nav-item-content'
          classes={{primary: clsx('nav-item-text')}}
          primary={item.title}
        />
        <IconButton
          className='nav-item-icon-arrow-btn'
          sx={{p: 0, mr: 0.75}}
          disableRipple
          size='large'
        >
          <Icon className='nav-item-icon-arrow' color='inherit'>
            {open
              ? 'expand_more'
              : theme.direction === 'ltr'
              ? 'chevron_right'
              : 'chevron_left'}
          </Icon>
        </IconButton>
      </VerticalCollapseItem>

      {item.children && (
        <Collapse in={open} className='collapse-children'>
          {item.children.map((item) => (
            <React.Fragment key={item.layoutid}>
              {item.menutype === 'collapse' && (
                <VerticalCollapse item={item} level={level + 1} />
              )}

              {item.menutype === 'item' && (
                <VerticalItem item={item} level={level + 1} />
              )}
            </React.Fragment>
          ))}
        </Collapse>
      )}
    </>
  );
};

VerticalCollapse.propTypes = {
  item: PropTypes.shape({
    layoutid: PropTypes.string.isRequired,
    title: PropTypes.string,
    menutype: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    permittedrole: PropTypes.oneOf(Object.values(BUSINESS_ROLE)),
    children: PropTypes.array,
  }),
  level: PropTypes.number,
};

export default React.memo(VerticalCollapse);
